import React, { useMemo, useRef, useState } from 'react';
import { Formik } from 'formik';
import { PropTypes } from 'prop-types';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Col, Row, Tooltip, message as toaster } from 'antd';
import { Form, Select, DatePicker } from 'formik-antd';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { Box, Button, FormInput, Text } from 'atoms';
import { ACL } from 'components/common';
import { useQuery } from 'hooks';
import { getClientList } from 'store/clientSlice';
import { createVendorBill, getListofInvoice } from 'store/vendorBillSlice';
import {
	createVendorBillV2,
	getListofInvoiceV2,
	bulkFetchVendorBillTripsV2,
} from 'store/vendorBillSliceV2';
import { getProjectListV2 } from 'store/projectSliceV2';
import { getUIVersion } from 'utils/router-static-data';
import { AntdTable } from 'components/custom-table/Style';
import BulkTripFetchModal, { BULK_TRIP_IDS_ENTITY } from 'componentsv2/common/BulkTripFetchModal';
import UnfetchedTripsPill from 'componentsv2/common/UnfetchedTrips';
import { DATE_FORMAT, DAY_DATE_FORMAT } from 'utils';

const { RangePicker } = DatePicker;

const RangeDatePickers = styled(RangePicker)`
	width: 100%;
`;
const FormSchema = Yup.object({
	bill_period: Yup.array()
		.transform((value) => (value === null ? undefined : value))
		.required()
		.label('Bill Period'),
	vendor_id: Yup.string().required().label('Vendor Id'),
	project_id: Yup.string().label('Project Id'),
	trip_type: Yup.array().label('Trip Type'),
});

const BulkFetchButton = styled(Button)`
	background: #fff;
	border: 1px solid #18405d;
	color: #18405d;
	border-radius: 4px;

	&:hover:enabled {
		background: #f1f5f9;
	}

	&:disabled {
		background: #fff;
		border-color: #d1d5db;
		color: #9ca3af;
	}
`;

const CancelButton = styled(Button)`
	background: transparent;
	border: 1px solid rgba(211, 47, 47, 0.5);
	color: #d32f2f;
	border-radius: 4px;
`;

// Old trip type options - commented out
// const trip_type_data = [
// 	{ label: 'Regular', value: 'regular' },
// 	{ label: 'Adhoc', value: 'adhoc' },
// ];
// trip_type_data.unshift({
// 	value: '',
// 	label: 'All',
// });

// New trip type options for both old and new UI
const trip_type_data = [
	{ label: 'Intercity Ad-Hoc', value: 'intercity_adhoc' },
	{ label: 'Intracity Ad-Hoc', value: 'intracity_adhoc' },
	{ label: 'Intracity Regular', value: 'intracity_regular' },
];

function getLocalDate(date) {
	// console.log(moment(date).add(330, 'minutes').format(DateFormat));
	// console.log(moment.utc(date, 'MM/DD/YYYY HH:mm').toDate());

	if (date) {
		return moment(date).add(330, 'minutes').format(DAY_DATE_FORMAT);
	}
	return '';
}

const columns = [
	{
		title: 'Trip Date',
		dataIndex: 'reporting_time',
		render: (reporting_time) => <Text overFlow="hidden">{getLocalDate(reporting_time)}</Text>,
	},
	{
		title: 'Trip ID',
		dataIndex: 'id',
	},
	{
		title: 'Trip Type',
		dataIndex: 'trip_type',
	},
	{
		title: 'Project Name',
		dataIndex: 'project_name',
	},
	{
		title: 'Vehicle Type (Vehicle No)',
		dataIndex: 'vehicle_type',
		render: (bill_period, record) => (
			<Text overFlow="hidden">
				{bill_period} - {record?.vehicle_number}
			</Text>
		),
	},
	{
		title: 'Vendor Rate',
		dataIndex: 'vendor_trip_cost',
	},
	{
		title: 'Payable Amount',
		dataIndex: 'vendor_trip_cost',
	},
];

export default function VendorBillGenerationForm({ cities, vendor_data }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const formRef = useRef();
	const [query] = useQuery();
	const uiVersion = getUIVersion();
	const useV2API = uiVersion === 'new';
	const [vendor_bill_data, setVendorBillData] = useState('');
	const [isTableMode, SetisTableMode] = useState(false);
	const [projectList, setProjectList] = useState([]);
	const [selectionType, setSelectionType] = useState([]);
	const [totalval, setTotalval] = useState('');
	const [dates, setDates] = useState(null);
	const [bulkOpen, setBulkOpen] = useState(false);
	const [bulkResult, setBulkResult] = useState(null);

	const initialValues = useMemo(
		() => ({
			bill_period: undefined,
			city: undefined,
			vendor_id: undefined,
			project_id: undefined,
			trip_type: undefined,
		}),
		[]
	);

	function getFormattedDate(val) {
		const fomatedDate = moment.utc(val).format(DATE_FORMAT);
		const validData = `${fomatedDate} 00:00`;
		return moment.utc(validData).subtract(330, 'minutes').format('MM/DD/YYYY HH:mm');
	}

	function getFormattedEndDate(val) {
		const fomatedDate = moment.utc(val).format(DATE_FORMAT);
		const validData = `${fomatedDate} 23:59`;
		return moment.utc(validData).subtract(330, 'minutes').format('MM/DD/YYYY HH:mm');
	}

	function getV2StartDate(val) {
		return moment(val).format('YYYY-MM-DDT00:00:00');
	}

	function getV2EndDate(val) {
		return moment(val).format('YYYY-MM-DDT23:59:59');
	}
	const submitValues = (values) => {
		if (selectionType.length > 0) {
			const { bill_period, city, vendor_id, ...rest } = values;
			console.log(values, rest);

			const start_date = useV2API
				? getV2StartDate(bill_period[0])
				: moment(bill_period[0]).format(DATE_FORMAT);
			const end_date = useV2API
				? getV2EndDate(bill_period[1])
				: moment(bill_period[1]).format(DATE_FORMAT);
			const month = `${moment(bill_period[0]).format(DATE_FORMAT)} - ${moment(
				bill_period[1]
			).format(DATE_FORMAT)}`;

			const payload = {
				create_type: 'vendor_bill',
				data: {
					trip_ids: selectionType,
					bill_start_time: start_date,
					bill_end_time: end_date,
					bill_period: month,
					city,
					vendor_id,
				},
			};

			const createAction = useV2API
				? createVendorBillV2({ req_data: payload.data })
				: createVendorBill({ data: payload });

			dispatch(createAction)
				.then((res) => {
					console.log(res);
					window.location.replace(`${window.origin}/vendor-bill-edting/${res.data.id}`);
				})
				.catch(() => {});
		} else {
			toaster.error('please select trips');
		}
	};

	const showTrips = (values) => {
		const { bill_period, city, vendor_id, project_id, trip_type, ...rest } = values;
		console.log(values, rest);

		const start_date = useV2API
			? getV2StartDate(bill_period[0])
			: getFormattedDate(bill_period[0]);
		const end_date = useV2API
			? getV2EndDate(bill_period[1])
			: getFormattedEndDate(bill_period[1]);
		const month = `${moment(bill_period[0]).format(DATE_FORMAT)} - ${moment(
			bill_period[1]
		).format(DATE_FORMAT)}`;
		console.log(start_date, end_date);
		const payload = {
			start_date,
			end_date,
			bill_period: month,
			city,
			vendor_id,
			project_id,
			// Send as array directly: ["intercity_adhoc", "intracity_adhoc"]
			trip_type: Array.isArray(trip_type) && trip_type.length > 0 ? trip_type : undefined,
			limit: 1000,
			offset: 0,
		};

		const tripAction = useV2API
			? getListofInvoiceV2({ data: payload })
			: getListofInvoice({ data: payload });

		dispatch(tripAction)
			.then((res) => {
				console.log(res);
				// Selection is intentionally NOT cleared here — a re-search keeps
				// whatever was already ticked, as it always has. Only the bulk
				// summary is dropped, since it described the previous result set.
				if (res?.rows.length > 0) {
					SetisTableMode(true);
					setVendorBillData(res?.rows);
					setBulkResult(null);
				} else {
					SetisTableMode(false);
					toaster.error('No Trips Available');
					setVendorBillData([]);
					setBulkResult(null);
				}
			})
			.catch(() => {});
	};
	const handleBulkFetch = async (values, file) => {
		const res = await dispatch(
			bulkFetchVendorBillTripsV2({
				filter_by: { vendor_id: values.vendor_id },
				entity: BULK_TRIP_IDS_ENTITY,
				file,
			})
		);
		const rows = res?.rows || [];
		setVendorBillData(rows);
		SetisTableMode(true);
		const keys = rows.map((r) => r.id);
		setSelectionType(keys);
		getSelectedValues(keys, rows);
		setBulkResult(res?.bulkFetch || null);
		if (!rows.length) toaster.warning('No trips could be fetched from the uploaded file.');
	};

	function handleCitiesChange(e, setFieldValue) {
		if (setFieldValue) {
			setFieldValue('trip_type', undefined);
		}

		if (useV2API) {
			dispatch(
				getProjectListV2({
					filter_by: { city: e },
					pagination: { per_page: 100 },
				})
			).then((res) => {
				const pro_Data = (res?.rows || []).map((item) => ({
					label: item?.project_name || item?.project_code || item?.project_name,
					value: item?.id,
				}));

				const jsonObject = pro_Data.map(JSON.stringify);
				const uniqueSet = new Set(jsonObject);
				const uniqueArray = Array.from(uniqueSet).map(JSON.parse);
				uniqueArray.unshift({
					value: '',
					label: 'All',
				});
				setProjectList(uniqueArray);
			});
		} else {
			dispatch(
				getClientList({
					search_type: 'project',
					filter_by: {
						city: e,
					},
				})
			).then((res) => {
				const pro_Data = res?.data?.map((item) => ({
					label: item?.project_location_code,
					value: item?.id,
				}));

				const jsonObject = pro_Data.map(JSON.stringify);
				console.log(jsonObject);
				const uniqueSet = new Set(jsonObject);
				const uniqueArray = Array.from(uniqueSet).map(JSON.parse);
				uniqueArray.unshift({
					value: '',
					label: 'All',
				});
				console.log(uniqueArray);
				setProjectList(uniqueArray);
			});
		}
	}

	function handleVendorsChange(e, setFieldValue) {
		if (setFieldValue) {
			setFieldValue('vehicle_type', e);
		}
	}
	const [selectedCount, setselectedCount] = useState(0);
	function getSelectedValues(rowValues, rows) {
		setselectedCount(rowValues.length);

		const source = rows || vendor_bill_data;
		const pro_Data = source?.map((item) => ({
			value: item?.vendor_trip_cost,
			id: item?.id,
		}));
		// const newArray = [];
		// eslint-disable-next-line no-unused-vars
		let totalValue = 0;
		rowValues.forEach((key) => {
			// eslint-disable-next-line no-restricted-syntax, guard-for-in
			for (const objectkey in pro_Data) {
				// if(key === )
				if (key === pro_Data[objectkey]?.id) {
					// eslint-disable-next-line no-unsafe-optional-chaining
					totalValue += parseFloat(pro_Data[objectkey]?.value);
					setTotalval(Number(totalValue).toFixed(2));
				}
			}
		});
		console.log(totalValue);
	}
	// Controlled in V2 only, where bulk fetch has to pre-check the rows it just
	// pulled in. V1 keeps the uncontrolled selection it has always had.
	const rowSelection = {
		...(useV2API ? { selectedRowKeys: selectionType } : {}),
		onChange: (newSelectedRowKeys) => {
			setSelectionType(newSelectedRowKeys);
			getSelectedValues(newSelectedRowKeys);
		},
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={FormSchema}
			onSubmit={showTrips}
			enableReinitialize
			innerRef={formRef}
			initialTouched={{
				zone: true,
				state: true,
				city: true,
			}}
		>
			{({ values, errors, setFieldValue }) => (
				<Box>
					<ACL action="update" module="project"></ACL>
					<Form className={query.get('isEdit') === 'false' ? 'disabled-div' : ''}>
						{console.log(values, errors)}
						<Box padding="1.5rem 2rem" background="#fff">
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="bill_period" label="Bill Period *">
										<RangeDatePickers
											name="bill_period"
											format="YYYY-MM-DD"
											onCalendarChange={(val) => setDates(val)}
											onOpenChange={(open) => !open && setDates(null)}
											disabledDate={(current) => {
												if (current && current > moment().endOf('day')) {
													return true;
												}
												if (dates && dates[0] && !dates[1]) {
													const startDate = dates[0];
													const endOfMonth = startDate
														.clone()
														.endOf('month');
													return (
														current.isBefore(startDate, 'day') ||
														current.isAfter(endOfMonth, 'day')
													);
												}
												return false;
											}}
											showTime={false}
										/>
									</FormInput>
								</Col>
								<Col lg={12}>
									<FormInput name="vendor_id" label="Select Vendor *">
										<Select
											name="vendor_id"
											showSearch
											allowClear
											filterOption={(input, option) =>
												option.label
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
											options={vendor_data}
											placeholder="Select"
											onChange={(e) => handleVendorsChange(e, setFieldValue)}
										/>
									</FormInput>
								</Col>
							</Row>
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="city" label="Select City">
										<Select
											showSearch
											allowClear
											name="city"
											filterOption={(input, option) =>
												option.label
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
											options={cities}
											placeholder="Select"
											onChange={(e) => handleCitiesChange(e, setFieldValue)}
										/>
									</FormInput>
								</Col>

								<Col lg={12}>
									<FormInput name="project_id" label="Select Project ">
										<Select
											showSearch
											allowClear
											name="project_id"
											filterOption={(input, option) =>
												option.label
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
											placeholder="Select"
											options={projectList}
										/>
									</FormInput>
								</Col>
							</Row>
							<Row gutter={16} justify="space-between">
								<Col lg={12}>
									<FormInput name="trip_type" label="Select Trip Type">
										<Select
											name="trip_type"
											mode="multiple"
											showSearch
											allowClear
											filterOption={(input, option) =>
												option.label
													.toLowerCase()
													.indexOf(input.toLowerCase()) >= 0
											}
											placeholder="Select"
											options={trip_type_data}
										/>
									</FormInput>
								</Col>
							</Row>
							<Box backgroundColor="#f5f5f5" textAlign="right" padding="1.5rem">
								<CancelButton
									onClick={() => navigate('/vendor-invoices')}
									type="button"
								>
									Cancel
								</CancelButton>
								{useV2API && (
									<Tooltip
										title={
											values.vendor_id
												? ''
												: 'Select a Vendor to enable bulk fetch'
										}
									>
										{/* span keeps the tooltip alive when disabled */}
										<span>
											<BulkFetchButton
												type="button"
												disabled={!values.vendor_id}
												onClick={() => setBulkOpen(true)}
											>
												Bulk Fetch
											</BulkFetchButton>
										</span>
									</Tooltip>
								)}
								<Button borderRadius="4px" backgroundColor="#18405d" type="submit">
									Search Trips
								</Button>
							</Box>
						</Box>
					</Form>

					{useV2API && (
						<BulkTripFetchModal
							open={bulkOpen}
							onCancel={() => setBulkOpen(false)}
							onUpload={(file) => handleBulkFetch(values, file)}
							contextLabel="the selected vendor"
						/>
					)}

					{isTableMode && (
						<Box>
							<Box mt={3}>
								<Box textAlign="left" mb={2}>
									<UnfetchedTripsPill bulkResult={bulkResult} context="vendor" />
								</Box>
								<AntdTable
									rowKey={(rowItem) => rowItem.id}
									rowSelection={{
										type: 'checkbox',
										...rowSelection,
									}}
									columns={columns}
									dataSource={vendor_bill_data}
								/>
							</Box>
							<Box backgroundColor="#f5f5f5" textAlign="right" padding="1.5rem">
								{selectedCount > 0 && (
									<Box className="total-container">
										<span className="bill-stats">
											<span className="count"> {selectedCount}</span>
											{'\n'}
											<div>Total Trips</div>
										</span>
										<span className="bill-stats">
											<span className="count"> {totalval}</span>
											{'\n'}
											<div>Total Payable</div>
										</span>
									</Box>
								)}
								<CancelButton
									onClick={() => navigate('/vendor-invoices')}
									type="button"
								>
									Cancel
								</CancelButton>
								<Button
									borderRadius="4px"
									backgroundColor="#18405d"
									type="button"
									onClick={() => submitValues(values)}
								>
									Submit
								</Button>
							</Box>
						</Box>
					)}
				</Box>
			)}
		</Formik>
	);
}

VendorBillGenerationForm.propTypes = {
	cities: PropTypes.array,
	vendor_data: PropTypes.array,
};
